// src/hooks/useMenuItemActions.ts
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchMenuItemDetailsRequest } from '../redux/slices/menuItemDetailsSlice';
import { addToCart, updateQuantity, removeFromCart } from '../redux/slices/cartSlice';
import {
  openModifierModal,
  closeModifierModal,
  openRepeatCustomisationModal,
  closeRepeatCustomisationModal,
  openDeleteVariantModal,
  closeDeleteVariantModal,
  setSelectedCartItem,
} from '../redux/slices/modalSlice';
import { findCartItemVariants } from '../utils/cartUtils';
import { transformModifierSelectionsForCart } from '../utils/modifierTransformUtils';
import { createCartItem } from '../utils/cartUtils';

export const useMenuItemActions = () => {
  const dispatch = useAppDispatch();
  const { items: cartItems } = useAppSelector((state) => state.cart);
  const { modifiers: modifiersList } = useAppSelector((state) => state.menuItemDetails);
  const modalState = useAppSelector((state) => state.modal);
  
  // State for handling no-modifier items
  const [pendingNoModifierItem, setPendingNoModifierItem] = useState<any | null>(null);
  const menuItemDetailsItem = useAppSelector((state) => state.menuItemDetails.item);

  // Auto-add to cart when menuItemDetails is fetched for no-modifier items
  useEffect(() => {
    if (pendingNoModifierItem && menuItemDetailsItem) {
      const itemData = menuItemDetailsItem;
      
      // Enrich menu item with posReferenceId from API
      const enrichedItem = {
        ...pendingNoModifierItem,
        posReferenceId: itemData.posReferenceId
      };
      
      // Add to cart with complete data
      const cartItem = createCartItem(enrichedItem, null, [], 1);
      dispatch(addToCart(cartItem));
      
      // Clear pending item
      setPendingNoModifierItem(null);
    }
  }, [menuItemDetailsItem, pendingNoModifierItem, dispatch]);

  // ========================================
  // 1. ADD ITEM FROM MENU/HOME PAGE
  // ========================================
  const handleAddItemClick = useCallback(
    (menuItem: any) => {
      console.log('➕ [useMenuItemActions] handleAddItemClick called');
      console.log('📦 Menu Item:', menuItem);
      console.log('🛒 Current Cart Items:', cartItems);
      
      // Check if item already in cart
      const existingVariants = findCartItemVariants(menuItem.pk_id.toString(), cartItems);
      console.log('🔍 Existing Variants:', existingVariants);

      if (existingVariants.length > 0) {
        // Item exists in cart
        const hasModifiers = menuItem.modifiers && menuItem.modifiers.length > 0;
        console.log('🎛️ Has Modifiers:', hasModifiers);

        if (hasModifiers) {
          // Has modifiers - show RepeatCustomisationModal
          console.log('📋 Opening RepeatCustomisationModal');
          dispatch(
            openRepeatCustomisationModal({
              cartItem: existingVariants[existingVariants.length - 1],
              menuItem: menuItem,
            })
          );
          return;
        } else {
          // No modifiers - check if has posReferenceId
          const existingItem = existingVariants[0];
          
          if (existingItem.item?.posReferenceId) {
            // Has posReferenceId - just increment
            console.log('➕ Incrementing quantity for existing item');
            dispatch(
              updateQuantity({
                cartItemId: existingItem.cartItemId,
                quantity: existingItem.quantity + 1,
              })
            );
          } else {
            // Missing posReferenceId - fetch details first
            console.log('🔄 Fetching details to update existing item');
            setPendingNoModifierItem(menuItem);
            dispatch(fetchMenuItemDetailsRequest(menuItem.pk_id));
          }
          return;
        }
      }

      // Not in cart - check if has modifiers
      const hasModifiers = menuItem.modifiers && menuItem.modifiers.length > 0;
      console.log('🆕 Item not in cart. Has Modifiers:', hasModifiers);

      if (!hasModifiers) {
        // NO MODIFIERS - Fetch details first to get posReferenceId
        console.log('🔄 Fetching details for no-modifier item');
        setPendingNoModifierItem(menuItem);
        dispatch(fetchMenuItemDetailsRequest(menuItem.pk_id));
        return;
      }

      // HAS MODIFIERS - Fetch details and open modal
      console.log('🔄 Fetching item details and opening modifier modal');
      dispatch(fetchMenuItemDetailsRequest(menuItem.pk_id));
      dispatch(openModifierModal({ item: menuItem, editMode: false }));
    },
    [cartItems, dispatch]
  );

  // ========================================
  // 2. INCREASE QUANTITY FROM CART
  // ========================================
  const handleIncreaseQuantityClick = useCallback(
    (cartItem: any) => {
      console.log('➕ [useMenuItemActions] handleIncreaseQuantityClick called');
      console.log('🛒 Cart Item:', cartItem);
      
      const hasModifiers = cartItem.selected?.modifiers?.length > 0;
      console.log('🎛️ Has Modifiers:', hasModifiers);

      if (hasModifiers) {
        console.log('📋 Opening RepeatCustomisationModal');
        // Has modifiers - show RepeatCustomisationModal
        dispatch(
          openRepeatCustomisationModal({
            cartItem: cartItem,
            menuItem: cartItem.item,
          })
        );
      } else {
        // No modifiers - just increment
        dispatch(
          updateQuantity({
            cartItemId: cartItem.cartItemId,
            quantity: cartItem.quantity + 1,
          })
        );
      }
    },
    [dispatch]
  );

  // ========================================
  // 2A. INCREASE QUANTITY FROM MENU CARD (Wrapper for menu items)
  // ========================================
  const handleMenuItemIncrement = useCallback(
    (menuItem: any) => {
      console.log('➕ [useMenuItemActions] handleMenuItemIncrement called');
      console.log('📦 Menu Item:', menuItem);
      
      // Find cart item using menu item ID
      const existingVariants = findCartItemVariants(menuItem.pk_id.toString(), cartItems);
      console.log('🔍 Found cart variants:', existingVariants);
      
      if (existingVariants.length > 0) {
        // Call the cart-based handler with the cart item
        handleIncreaseQuantityClick(existingVariants[0]);
      } else {
        console.log('⚠️ No cart item found for menu item');
      }
    },
    [cartItems, handleIncreaseQuantityClick]
  );

  // ========================================
  // 3. DECREASE QUANTITY FROM CART
  // ========================================
  const handleDecreaseQuantityClick = useCallback(
    (cartItem: any) => {
      console.log('➖ [useMenuItemActions] handleDecreaseQuantityClick called');
      console.log('🛒 Cart Item:', cartItem);
      console.log('🔢 Current Quantity:', cartItem.quantity);
      
      if (cartItem.quantity > 1) {
        console.log('⬇️ Decrementing quantity');
        // Quantity > 1 - direct decrement
        dispatch(
          updateQuantity({
            cartItemId: cartItem.cartItemId,
            quantity: cartItem.quantity - 1,
          })
        );
        return;
      }

      // Quantity = 1 - check for multiple variants
      const itemId = cartItem.item.pk_id || cartItem.item.id;
      const allVariants = findCartItemVariants(itemId.toString(), cartItems);

      if (allVariants.length > 1) {
        // Multiple variants - open DeleteVariantModal
        dispatch(
          openDeleteVariantModal({
            variants: allVariants,
            menuItem: cartItem.item,
          })
        );
      } else {
        // Single variant - direct remove
        dispatch(removeFromCart(cartItem.cartItemId));
      }
    },
    [cartItems, dispatch]
  );

  // ========================================
  // 3A. DECREASE QUANTITY FROM MENU CARD (Wrapper for menu items)
  // ========================================
  const handleMenuItemDecrement = useCallback(
    (itemId: string) => {
      console.log('➖ [useMenuItemActions] handleMenuItemDecrement called');
      console.log('🆔 Item ID:', itemId);
      
      // Find cart item using item ID
      const existingVariants = findCartItemVariants(itemId, cartItems);
      console.log('🔍 Found cart variants:', existingVariants);
      
      if (existingVariants.length > 0) {
        // Call the cart-based handler with the cart item
        handleDecreaseQuantityClick(existingVariants[0]);
      } else {
        console.log('⚠️ No cart item found for item ID');
      }
    },
    [cartItems, handleDecreaseQuantityClick]
  );

  // ========================================
  // 4. EDIT CART ITEM
  // ========================================
  const handleEditCartItem = useCallback(
    (cartItem: any) => {
      console.log('✏️ [useMenuItemActions] handleEditCartItem called');
      console.log('🛒 Cart Item:', cartItem);
      
      const itemId = cartItem.item.pk_id || cartItem.item.id;
      console.log('🆔 Item ID:', itemId);
      
      dispatch(setSelectedCartItem(cartItem));
      dispatch(fetchMenuItemDetailsRequest(itemId));
      dispatch(openModifierModal({ item: cartItem.item, editMode: true }));
      console.log('📋 Opening modifier modal in edit mode');
    },
    [dispatch]
  );

  // ========================================
  // 5. REPEAT CUSTOMISATION
  // ========================================
  const handleRepeatCustomisation = useCallback(() => {
    console.log('🔁 [useMenuItemActions] handleRepeatCustomisation called');
    console.log('🛒 Selected Cart Item:', modalState.selectedCartItem);
    
    if (modalState.selectedCartItem) {
      console.log('➕ Incrementing quantity for existing customisation');
      dispatch(
        updateQuantity({
          cartItemId: modalState.selectedCartItem.cartItemId,
          quantity: modalState.selectedCartItem.quantity + 1,
        })
      );
      dispatch(closeRepeatCustomisationModal());
      console.log('✅ Quantity updated and modal closed');
    }
  }, [modalState.selectedCartItem, dispatch]);

  // ========================================
  // 6. CHOOSE NEW CUSTOMISATION
  // ========================================
  const handleChooseNewCustomisation = useCallback(() => {
    console.log('🆕 [useMenuItemActions] handleChooseNewCustomisation called');
    console.log('📦 Current Menu Item:', modalState.currentMenuItem);
    
    if (modalState.currentMenuItem) {
      const itemId = modalState.currentMenuItem.pk_id || modalState.currentMenuItem.id;
      console.log('🆔 Item ID:', itemId);
      console.log('🔄 Fetching item details and opening modifier modal');
      
      dispatch(fetchMenuItemDetailsRequest(itemId));
      dispatch(closeRepeatCustomisationModal());
      dispatch(openModifierModal({ item: modalState.currentMenuItem, editMode: false }));
    }
  }, [modalState.currentMenuItem, dispatch]);

  // ========================================
  // 7. ADD TO CART FROM MODIFIER MODAL
  // ========================================
  const handleAddToCartFromModal = useCallback(
    (item: any, quantity: number, modifierSelections: any) => {
      console.log('🛒 [useMenuItemActions] handleAddToCartFromModal called');
      console.log('📦 Item:', item);
      console.log('🔢 Quantity:', quantity);
      console.log('🎛️ Modifier Selections:', modifierSelections);
      console.log('📋 Modifiers List:', modifiersList);
      console.log('🛒 Current Cart Items:', cartItems);
      
      const selectedModifiers = transformModifierSelectionsForCart(
        modifierSelections,
        modifiersList
      );
      console.log('✅ Transformed Modifiers:', selectedModifiers);
      
      const newCartItem = createCartItem(item, null, selectedModifiers, quantity);
      console.log('🛍️ Created Cart Item:', newCartItem);
      console.log('🎫 Cart Item ID:', newCartItem.cartItemId);
      
      // Check if this exact configuration already exists in cart
      const existingItem = cartItems.find(ci => ci.cartItemId === newCartItem.cartItemId);
      console.log('🔍 Found existing item:', existingItem);
      
      if (existingItem) {
        // SAME modifiers - increase quantity of existing item
        console.log('➕ Merging with existing item - Old quantity:', existingItem.quantity, 'Adding:', quantity);
        dispatch(updateQuantity({
          cartItemId: existingItem.cartItemId,
          quantity: existingItem.quantity + quantity
        }));
        console.log('✅ Quantity updated successfully');
      } else {
        // DIFFERENT modifiers or new item - add as separate cart item
        console.log('🆕 Adding as new cart item');
        dispatch(addToCart(newCartItem));
        console.log('✅ Item added to cart successfully');
      }
      
      dispatch(closeModifierModal());
    },
    [dispatch, modifiersList, cartItems]
  );

  // ========================================
  // 8. UPDATE CART ITEM (EDIT MODE)
  // ========================================
  const handleUpdateCartItem = useCallback(
    (item: any, quantity: number, modifierSelections: any) => {
      console.log('✏️ [useMenuItemActions] handleUpdateCartItem called');
      console.log('📦 Item:', item);
      console.log('🔢 Quantity:', quantity);
      console.log('🎛️ Modifier Selections:', modifierSelections);
      console.log('🛒 Selected Cart Item:', modalState.selectedCartItem);
      
      if (modalState.selectedCartItem) {
        // Remove old cart item
        console.log('🗑️ Removing old cart item:', modalState.selectedCartItem.cartItemId);
        dispatch(removeFromCart(modalState.selectedCartItem.cartItemId));

        // Add updated cart item
        const selectedModifiers = transformModifierSelectionsForCart(
          modifierSelections,
          modifiersList
        );
        console.log('✅ Transformed Modifiers:', selectedModifiers);
        
        const cartItem = createCartItem(item, null, selectedModifiers, quantity);
        console.log('🛍️ Created Updated Cart Item:', cartItem);
        
        dispatch(addToCart(cartItem));
        dispatch(closeModifierModal());
        console.log('✅ Cart item updated successfully');
      }
    },
    [dispatch, modifiersList, modalState.selectedCartItem]
  );

  // ========================================
  // 9. DELETE VARIANT
  // ========================================
  const handleDeleteVariant = useCallback(
    (cartItemId: string) => {
      console.log('🗑️ [useMenuItemActions] handleDeleteVariant called');
      console.log('🎫 Cart Item ID:', cartItemId);
      
      dispatch(removeFromCart(cartItemId));
      dispatch(closeDeleteVariantModal());
      console.log('✅ Variant removed and modal closed');
    },
    [dispatch]
  );

  return {
    // Modal states
    isModifierModalOpen: modalState.isModifierModalOpen,
    isRepeatCustomisationModalOpen: modalState.isRepeatCustomisationModalOpen,
    isDeleteVariantModalOpen: modalState.isDeleteVariantModalOpen,
    selectedCartItem: modalState.selectedCartItem,
    currentMenuItem: modalState.currentMenuItem,
    editMode: modalState.editMode,
    deleteVariants: modalState.deleteVariants,

    // Actions
    handleAddItemClick,
    handleIncreaseQuantityClick,
    handleDecreaseQuantityClick,
    handleMenuItemIncrement,      // NEW: Wrapper for menu cards
    handleMenuItemDecrement,      // NEW: Wrapper for menu cards
    handleEditCartItem,
    handleRepeatCustomisation,
    handleChooseNewCustomisation,
    handleAddToCartFromModal,
    handleUpdateCartItem,
    handleDeleteVariant,

    // Close handlers
    closeModifierModal: () => dispatch(closeModifierModal()),
    closeRepeatCustomisationModal: () => dispatch(closeRepeatCustomisationModal()),
    closeDeleteVariantModal: () => dispatch(closeDeleteVariantModal()),
  };
};
